import React from "react";
import { cn } from "./cn";

/**
 * Botón único de la app.
 *
 * Antes cada pantalla escribía su propio `px-4 py-2 bg-brand-500 ... rounded`,
 * con alturas, radios y estados disabled distintos. Acá quedan centralizados:
 * la jerarquía visual (primary > secondary > ghost) es la que comunica qué
 * acción es la principal en cada bloque.
 */
export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "soft"
  | "destructive"
  | "destructive-soft"
  | "success-soft";

export type ButtonSize = "sm" | "md" | "lg" | "icon" | "icon-sm";

const base =
  "relative inline-flex select-none items-center justify-center gap-2 whitespace-nowrap " +
  "rounded-control font-semibold transition-[background-color,border-color,color,box-shadow,transform] " +
  "duration-150 ease-premium active:translate-y-[0.5px] " +
  "disabled:pointer-events-none disabled:opacity-45";

const variants: Record<ButtonVariant, string> = {
  primary:
    "bg-brand-500 text-white shadow-soft hover:bg-brand-600 active:bg-brand-700 " +
    "dark:bg-brand-500 dark:hover:bg-brand-400",
  secondary:
    "border border-gray-200 bg-white text-gray-900 hover:bg-gray-50 active:bg-gray-100 " +
    "dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-white/5",
  ghost:
    "text-gray-700 hover:bg-gray-100 active:bg-gray-200 " +
    "dark:text-gray-200 dark:hover:bg-white/10 dark:active:bg-white/5",
  soft:
    "bg-brand-50 text-brand-600 hover:bg-brand-100 active:bg-brand-200 " +
    "dark:bg-navy-700 dark:text-white dark:hover:bg-white/15 dark:active:bg-white/10",
  destructive:
    "bg-red-600 text-white shadow-soft hover:bg-red-700 active:bg-red-800",
  "destructive-soft":
    "border border-red-200 bg-red-100 text-red-700 hover:bg-red-200 active:bg-red-300 " +
    "dark:border-red-700/40 dark:bg-red-700/15 dark:text-red-300 dark:hover:bg-red-700/25",
  "success-soft":
    "border border-green-200 bg-green-100 text-green-800 hover:bg-green-200 active:bg-green-300 " +
    "dark:border-green-700/40 dark:bg-green-700/20 dark:text-green-300 dark:hover:bg-green-700/30",
};

const sizes: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-[13px]",
  md: "h-9 px-3.5 text-sm",
  lg: "h-11 px-5 text-[15px]",
  icon: "h-9 w-9 p-0",
  "icon-sm": "h-8 w-8 p-0",
};

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Spinner = () => (
  <svg
    className="h-4 w-4 animate-spin"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="3"
    />
    <path
      className="opacity-90"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z"
    />
  </svg>
);

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "secondary",
      size = "md",
      loading = false,
      fullWidth = false,
      leftIcon,
      rightIcon,
      className,
      children,
      disabled,
      type = "button",
      ...rest
    },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        base,
        variants[variant],
        sizes[size],
        fullWidth && "w-full",
        className
      )}
      {...rest}
    >
      {loading ? <Spinner /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  )
);

Button.displayName = "Button";

export default Button;
